// Editor del "rate card" de cobro de un contrato (contract.pricing). Edita los
// campos que usa el motor de tarifación de la Nota de cobro: precio base por
// defecto, zonas (por nombre de comuna), recargos por tamaño y reglas. Guarda con
// PATCH /contracts/:id enviando el pricing mergeado (preserva campos legacy).
//
// ES UN OVERRIDE, no la única tarifa: desde Configuración → Cobro a comercios el operador
// define una tarifa base que aplica a TODOS sus comercios, y lo que se cargue acá le gana
// SOLO en lo que defina. Se muestra lo heredado en cada campo porque, si no, un campo
// vacío parece "no se cobra" cuando en realidad se cobra el valor del operador.
//
// También edita la tarifa propia de un HOLDING (`subject="holding"`): misma forma que la de un
// contrato, pero se guarda en billing con `onSubmit` (commerce-pricing-config/holdings/:id)
// en vez de PATCH /contracts/:id. Solo cambian los textos y el destino del guardado.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Plus, Trash2, Save } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { updateContract } from "../../../api/contracts";
import { getCommercePricingConfig } from "../../../api/billing";
import { showToast } from "../../../utils/showToast";
import CommuneRatesEditor, {
  communeRatesFromApi,
  communeRatesToApi,
} from "../billing/components/CommuneRatesEditor";
import useOperatorCoverage from "../../../hooks/useOperatorCoverage";
import useAuth from "../../../hooks/useAuth";
import { resolveOperatorCurrency } from "../cobro/utils/cobroHelpers";

const inputCls =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";
// Categorías de sobre-tamaño disponibles (overSizedEvidence.category).
const SIZE_CATEGORIES = ["XL", "2XL", "3XL"];
// Escalas de tarifa por volumen. "" (heredar) no está acá: no es un valor persistible.
const VOLUME_SCALES = ["flat", "weekly", "monthly"];
// Etiquetas para nombrar la escala HEREDADA. Se pasan `t` en vez de traducir al cargar el
// módulo: i18n todavía no está inicializado a nivel de módulo y la cadena quedaría en el
// idioma por defecto para siempre.
const SCALE_LABELS = {
  flat: (t) => t("tarifa plana"),
  weekly: (t) => t("escala semanal"),
  monthly: (t) => t("escala mensual"),
};
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Textos (claves i18n) que nombran a quién es la tarifa: un comercio (contrato) o un holding,
// cuya tarifa se aplica a los pedidos de todas sus empresas en su nota consolidada.
const SUBJECT_TEXT_LABELS = {
  contract: {
    intro:
      'Esto es una tarifa propia de este comercio. Lo que dejes vacío NO queda sin cobrar: se hereda de Configuración → Cobro a comercios.',
    scaleHint:
      'Las escalas y sus tramos se definen en Configuración → Cobro a comercios. Acá solo elegís a cuál se apega este comercio.',
    cycleHint:
      'Sus notas de cobro solo se pueden emitir cubriendo el ciclo completo. El asistente de cobro va a filtrar este comercio según el período que elijas.',
    zonesEclipse:
      'Este comercio tiene tarifas por comuna: esos pedidos conservan su precio y NO ven el escalonado. El tramo solo se aplica a los pedidos que hoy caen en el precio base.',
    noTiers:
      'La escala elegida no tiene tramos cargados, así que este comercio se cobra con la tarifa plana. Cargalos en Configuración → Cobro a comercios.',
    zonesDescription:
      'Precio cerrado para esas comunas, solo para este comercio. Le gana a la tarifa por comuna del operador.',
  },
  holding: {
    intro:
      'Esta es la tarifa propia del holding: se aplica a los pedidos de todas sus empresas en su nota consolidada. Lo que dejes vacío NO queda sin cobrar: se hereda de Configuración → Cobro a comercios.',
    scaleHint:
      'Las escalas y sus tramos se definen en Configuración → Cobro a comercios. Acá solo elegís a cuál se apega el holding: el volumen suma los pedidos de todas sus empresas.',
    cycleHint:
      'Su nota consolidada solo se puede emitir cubriendo el ciclo completo. El asistente de cobro va a filtrar el holding según el período que elijas.',
    zonesEclipse:
      'El holding tiene tarifas por comuna: esos pedidos conservan su precio y NO ven el escalonado. El tramo solo se aplica a los pedidos que hoy caen en el precio base.',
    noTiers:
      'La escala elegida no tiene tramos cargados, así que el holding se cobra con la tarifa plana. Cargalos en Configuración → Cobro a comercios.',
    zonesDescription:
      'Precio cerrado para esas comunas, solo para el holding. Le gana a la tarifa por comuna del operador.',
  },
};

/**
 * @param {{ contract: object, onSaved?: () => void, subject?: 'contract'|'holding',
 *           onSubmit?: (body: { pricing: object, pricingEffectiveFrom?: string }) => Promise<boolean> }} props
 *   `contract` trae `pricing`, `pricingVersion` y `pricingHistory` (un contrato o la vista de
 *   la tarifa de un holding). Con `onSubmit` el guardado lo hace quien llama (y avisa sus
 *   errores); devuelve si se guardó. Sin él se guarda en el contrato, como siempre.
 */
export default function RateCardEditor({ contract, onSaved, subject = 'contract', onSubmit }) {
  const { t } = useTranslation();
  const texts = SUBJECT_TEXT_LABELS[subject] ?? SUBJECT_TEXT_LABELS.contract;
  const pricing = contract?.pricing ?? {};
  const currentVersion = contract?.pricingVersion;
  const history = Array.isArray(contract?.pricingHistory)
    ? contract.pricingHistory
    : [];

  const [defaultBaseCost, setDefaultBaseCost] = useState(
    pricing.defaultBaseCost != null ? String(pricing.defaultBaseCost) : "",
  );
  // Inicializador PEREZOSO a propósito: `communeRatesFromApi` genera un id por fila para
  // la `key` de React, y sin el lazy se recalcularían en cada render, remontando el
  // editor entero (y perdiendo el foco mientras se escribe un precio).
  const [zones, setZones] = useState(() =>
    communeRatesFromApi(
      (Array.isArray(pricing.zonePricing) ? pricing.zonePricing : []).map((z) => ({
        // Los alias `comunas`/`communes` los sigue aceptando el motor de cobro
        // (cobro-rating.service.ts), así que se preservan al hidratar.
        communeNames: z.communeNames ?? z.comunas ?? z.communes ?? [],
        baseCost: z.baseCost,
      })),
    ),
  );
  const [sizes, setSizes] = useState(
    (Array.isArray(pricing.sizeSurcharges) ? pricing.sizeSurcharges : []).map((s) => ({
      category: s.category ?? "",
      multiplier: s.multiplier != null ? String(s.multiplier) : "1",
      additionalCost: s.additionalCost != null ? String(s.additionalCost) : "0",
    })),
  );
  const [rules, setRules] = useState(
    (Array.isArray(pricing.rules) ? pricing.rules : []).map((r) => ({
      label: r.label ?? "",
      kind: r.kind ?? "surcharge",
      calc: r.calc ?? "percent",
      // percent: se muestra como % (0.1 -> 10); flat: monto tal cual.
      value:
        r.calc === "percent"
          ? String(Math.round((num(r.value) || 0) * 1000) / 10)
          : String(r.value ?? ""),
      priority: r.priority != null ? String(r.priority) : "0",
    })),
  );
  const [saving, setSaving] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  // Tarifa base del operador, solo para MOSTRAR qué hereda este contrato. Si la lectura
  // falla se deja en null y la UI simplemente no muestra los heredados: no vale la pena
  // bloquear la edición del contrato porque no se pudo leer un dato informativo.
  const [inherited, setInherited] = useState(null);

  useEffect(() => {
    let alive = true;
    getCommercePricingConfig()
      .then((res) => {
        const body = res?.data;
        const cfg =
          body && typeof body === "object" && "data" in body ? body.data : body;
        if (alive) setInherited(cfg ?? null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Cobertura del operador autenticado, para acotar los selectores de comuna. Esta vista
  // no recibe el documento de la empresa por prop, así que se lee con el hook dedicado.
  const { user } = useAuth();
  const { coverageAreas, loading: coverageLoading } = useOperatorCoverage();
  // Guarda: solo se acota con la cobertura del usuario si el contrato es de SU empresa.
  // Si se puede afirmar que es de otro operador, no se filtra — mostrarle la cobertura
  // equivocada le ocultaría comunas que sí puede tarifar. Cuando el dato no viene (el
  // listado no siempre popula `logisticsCompanyId`) se filtra igual: esta vista es la del
  // operador, así que su cobertura es la que corresponde.
  const jwtCompanyId = user?.logisticCompany?._id ?? user?.logisticCompanyId ?? null;
  const contractCompanyId =
    contract?.logisticsCompanyId?._id ?? contract?.logisticsCompanyId ?? null;
  const foreignOperator =
    !!jwtCompanyId &&
    !!contractCompanyId &&
    String(jwtCompanyId) !== String(contractCompanyId);

  const inheritedDefault = inherited?.defaultBaseCost;
  const inheritedSizes = Array.isArray(inherited?.sizeSurcharges)
    ? inherited.sizeSurcharges
    : [];
  // Categorías que este contrato NO redefine: se heredan tal cual (el merge es POR
  // categoría, no por bloque).
  const ownCategories = new Set(
    sizes.map((s) => s.category.trim().toUpperCase()).filter(Boolean),
  );
  const inheritedOnlySizes = inheritedSizes.filter(
    (s) => !ownCategories.has(String(s?.category ?? "").toUpperCase()),
  );
  // Tratamiento del IVA de las tarifas. "" = sin IVA (comportamiento actual);
  // 'exclusive' = las tarifas son netas y se les agrega el IVA del país del operador;
  // 'inclusive' = las tarifas ya incluyen IVA (se desglosa en la nota de cobro).
  const [taxMode, setTaxMode] = useState(
    pricing.taxMode === "inclusive" || pricing.taxMode === "exclusive"
      ? pricing.taxMode
      : "",
  );

  // Escala de tarifa por volumen de ESTE comercio. "" = hereda la del operador.
  //
  // `'flat'` es un valor EXPLÍCITO y distinto de "": sin él no habría forma de dejar a un
  // comercio en precio plano cuando el default del operador es escalonado.
  const [volumeScale, setVolumeScale] = useState(
    VOLUME_SCALES.includes(pricing.volumeScale) ? pricing.volumeScale : "",
  );
  const inheritedScale = inherited?.defaultVolumeScale ?? "flat";
  const operatorHasWeekly = (inherited?.weeklyTiers?.length ?? 0) > 0;
  const operatorHasMonthly = (inherited?.monthlyTiers?.length ?? 0) > 0;
  const effectiveScale = volumeScale || inheritedScale;
  // Una escala sin tramos cargados en el operador no hace nada: el motor la degrada a
  // plana. Se avisa en vez de dejar creer que el comercio quedó escalonado.
  const scaleHasNoTiers =
    (effectiveScale === "weekly" && !operatorHasWeekly) ||
    (effectiveScale === "monthly" && !operatorHasMonthly);
  // El tramo reemplaza el PRECIO BASE, y la tarifa por comuna se resuelve antes. Un
  // contrato con todas sus comunas tarifadas no ve el escalonado en ningún pedido.
  const scaleEclipsedByZones = effectiveScale !== "flat" && zones.length > 0;

  const addRow = (setter, row) => setter((rows) => [...rows, row]);
  const setRow = (setter, i, patch) =>
    setter((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (setter, i) =>
    setter((rows) => rows.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    const newPricing = {
      ...pricing,
      // "" (sin IVA) se guarda como undefined -> se omite del pricing (legacy).
      taxMode: taxMode || undefined,
      // "" (heredar del operador) se omite del pricing: la ausencia ES la herencia. Un
      // `'flat'` guardado, en cambio, deja al comercio fuera del escalonado aunque el
      // operador cambie su default.
      volumeScale: volumeScale || undefined,
      defaultBaseCost:
        defaultBaseCost === "" ? null : Math.round(num(defaultBaseCost)),
      zonePricing: communeRatesToApi(zones),
      sizeSurcharges: sizes
        .filter((s) => s.category.trim())
        .map((s) => ({
          category: s.category.trim(),
          multiplier: num(s.multiplier) || 1,
          additionalCost: Math.round(num(s.additionalCost)),
        })),
      rules: rules
        .filter((r) => r.value !== "")
        .map((r, i) => ({
          id: `r${i + 1}`,
          label: r.label.trim() || (r.kind === "surcharge" ? t("Recargo") : t("Descuento")),
          kind: r.kind,
          calc: r.calc,
          value: r.calc === "percent" ? num(r.value) / 100 : Math.round(num(r.value)),
          priority: Math.round(num(r.priority)),
          stackable: true,
        })),
    };

    setSaving(true);
    try {
      const body = {
        pricing: newPricing,
        pricingEffectiveFrom: effectiveFrom || undefined,
      };
      if (onSubmit) {
        if (await onSubmit(body)) {
          showToast(t("Tarifas de cobro guardadas"), "success");
          onSaved?.();
        }
        return;
      }
      const res = await updateContract(contract._id, body);
      if (res?.error) {
        showToast(t("No se pudieron guardar las tarifas"), "error");
      } else {
        showToast(t("Tarifas de cobro guardadas"), "success");
        onSaved?.();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-2 rounded-md border border-border bg-bg-subtle p-3 text-xs text-fg-muted">
        <Info className="w-4 h-4 shrink-0 mt-px" />
        <span>{t(texts.intro)}</span>
      </div>

      {(currentVersion != null || history.length > 0) && (
        <div className="text-xs text-fg-muted">
          {t("Versión actual")}:{" "}
          <span className="font-medium text-fg">v{currentVersion ?? 1}</span>
          {history.length > 0 && <span> · {t("{{n}} en historial", { n: history.length })}</span>}
        </div>
      )}

      {/* Tratamiento de IVA */}
      <div>
        <label className="text-sm font-medium text-fg">
          {t("Tratamiento de IVA")}
        </label>
        <p className="text-xs text-fg-muted mb-1">
          {t("Define si las tarifas de abajo llevan IVA. La tasa se aplica según el país donde opera tu empresa (ej. Chile 19%).")}
        </p>
        <select
          className={`${inputCls} max-w-[340px]`}
          value={taxMode}
          onChange={(e) => setTaxMode(e.target.value)}
        >
          <option value="">
            {inherited?.taxMode
              ? t("Heredar del operador")
              : t("Sin IVA (actual)")}
          </option>
          <option value="exclusive">{t("Sin IVA incluido — se agrega el IVA del país")}</option>
          <option value="inclusive">{t("IVA incluido en las tarifas")}</option>
        </select>
      </div>

      {/* Precio base por defecto */}
      <div>
        <label className="text-sm font-medium text-fg">
          {t("Precio base por defecto")}
        </label>
        <p className="text-xs text-fg-muted mb-1">
          {inheritedDefault != null
            ? t("Se aplica a las comunas sin tarifa propia. Vacío = se hereda el precio base del operador ({{amount}}).", { amount: inheritedDefault })
            : t("Se aplica a las comunas sin tarifa propia. Vacío = esas comunas quedan “sin tarifar”.")}
        </p>
        <input
          type="number"
          min="0"
          className={`${inputCls} max-w-[200px]`}
          value={defaultBaseCost}
          onChange={(e) => setDefaultBaseCost(e.target.value)}
          placeholder={
            inheritedDefault != null
              ? t("Heredado: {{amount}}", { amount: inheritedDefault })
              : t("Ej: 3000")
          }
        />
      </div>

      {/* Escala de tarifa por volumen. Va pegada al precio base porque es lo que
          reemplaza: el tramo alcanzado pisa el precio base de todos los pedidos del ciclo. */}
      <div>
        <label className="text-sm font-medium text-fg">
          {t("Tarifa por volumen")}
        </label>
        <p className="text-xs text-fg-muted mb-1">{t(texts.scaleHint)}</p>
        <select
          className={`${inputCls} max-w-[340px]`}
          value={volumeScale}
          onChange={(e) => setVolumeScale(e.target.value)}
        >
          <option value="">
            {t("Heredar del operador ({{scale}})", {
              scale: SCALE_LABELS[inheritedScale]?.(t) ?? inheritedScale,
            })}
          </option>
          <option value="flat">{t("Tarifa plana — sin escalonado")}</option>
          <option value="weekly" disabled={!operatorHasWeekly}>
            {operatorHasWeekly
              ? t("Escala semanal (domingo a sábado)")
              : t("Escala semanal — el operador no tiene tramos cargados")}
          </option>
          <option value="monthly" disabled={!operatorHasMonthly}>
            {operatorHasMonthly
              ? t("Escala mensual (mes calendario)")
              : t("Escala mensual — el operador no tiene tramos cargados")}
          </option>
        </select>

        {effectiveScale !== "flat" && !scaleHasNoTiers && (
          <div className="flex items-start gap-2 text-xs text-fg-muted mt-2">
            <Info className="w-4 h-4 shrink-0 mt-px" />
            <span>{t(texts.cycleHint)}</span>
          </div>
        )}

        {/* El caso que más confunde: se eligió una escala y no aplica a nada, porque la
            tarifa por comuna se resuelve antes que el precio base que el tramo reemplaza. */}
        {scaleEclipsedByZones && (
          <div className="flex items-start gap-2 text-xs text-status-warning mt-2">
            <Info className="w-4 h-4 shrink-0 mt-px" />
            <span>{t(texts.zonesEclipse)}</span>
          </div>
        )}

        {scaleHasNoTiers && (
          <div className="flex items-start gap-2 text-xs text-status-warning mt-2">
            <Info className="w-4 h-4 shrink-0 mt-px" />
            <span>{t(texts.noTiers)}</span>
          </div>
        )}
      </div>

      {/* Zonas por comuna. Mismo editor que la tarifa base del operador
          (Configuración → Cobro a comercios): selectores acotados a la cobertura, en vez
          del texto libre por coma que había antes. */}
      <CommuneRatesEditor
        rows={zones}
        onChange={setZones}
        currency={resolveOperatorCurrency(user?.logisticCompany)}
        coverageAreas={foreignOperator ? [] : coverageAreas}
        coverageLoading={foreignOperator ? false : coverageLoading}
        showCoverageHint={!foreignOperator}
        title={t("Tarifas por comuna (override)")}
        description={t(texts.zonesDescription)}
      />

      {/* Recargos por tamaño */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-fg">
            {t("Recargos por tamaño (overSizedEvidence.category)")}
          </label>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Plus className="w-4 h-4" />}
            onClick={() =>
              addRow(setSizes, { category: "", multiplier: "1", additionalCost: "0" })
            }
          >
            {t("Agregar")}
          </Button>
        </div>
        {inheritedOnlySizes.length > 0 && (
          <p className="text-xs text-fg-muted mb-1">
            {t("Heredados del operador: {{list}}. Se aplican salvo que agregues esa categoría acá.", {
              list: inheritedOnlySizes
                .map((s) => `${s.category} (×${s.multiplier}${s.additionalCost ? ` + ${s.additionalCost}` : ""})`)
                .join(", "),
            })}
          </p>
        )}
        {sizes.length === 0 && inheritedOnlySizes.length === 0 && (
          <p className="text-xs text-fg-muted">{t("Sin recargos por tamaño.")}</p>
        )}
        {sizes.map((s, i) => (
          <div key={i} className="flex gap-2 mb-2 items-center flex-wrap">
            <select
              className={`${inputCls} max-w-[150px]`}
              value={s.category}
              onChange={(e) => setRow(setSizes, i, { category: e.target.value })}
            >
              <option value="">{t("Categoría…")}</option>
              {SIZE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
              {s.category && !SIZE_CATEGORIES.includes(s.category) && (
                <option value={s.category}>{s.category}</option>
              )}
            </select>
            <input
              type="number"
              step="0.1"
              min="0"
              className={`${inputCls} max-w-[120px]`}
              value={s.multiplier}
              onChange={(e) => setRow(setSizes, i, { multiplier: e.target.value })}
              placeholder={t("×Mult.")}
              title={t("Multiplicador sobre la base (2 = doble)")}
            />
            <input
              type="number"
              min="0"
              className={`${inputCls} max-w-[120px]`}
              value={s.additionalCost}
              onChange={(e) =>
                setRow(setSizes, i, { additionalCost: e.target.value })
              }
              placeholder={t("+ Fijo")}
              title={t("Cargo fijo adicional")}
            />
            <button
              type="button"
              className="h-9 px-2 text-fg-muted hover:text-status-error"
              onClick={() => removeRow(setSizes, i)}
              aria-label={t("Quitar")}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Reglas */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-fg">
            {t("Reglas automáticas (recargos / descuentos)")}
          </label>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Plus className="w-4 h-4" />}
            onClick={() =>
              addRow(setRules, {
                label: "",
                kind: "surcharge",
                calc: "percent",
                value: "",
                priority: "0",
              })
            }
          >
            {t("Agregar")}
          </Button>
        </div>
        {rules.length === 0 && (
          <p className="text-xs text-fg-muted">{t("Sin reglas automáticas.")}</p>
        )}
        {rules.map((r, i) => (
          <div key={i} className="flex gap-2 mb-2 items-center flex-wrap">
            <select
              className={`${inputCls} max-w-[130px]`}
              value={r.kind}
              onChange={(e) => setRow(setRules, i, { kind: e.target.value })}
            >
              <option value="surcharge">{t("Recargo")}</option>
              <option value="discount">{t("Descuento")}</option>
            </select>
            <input
              className={`${inputCls} min-w-[140px] flex-1`}
              value={r.label}
              onChange={(e) => setRow(setRules, i, { label: e.target.value })}
              placeholder={t("Concepto (Combustible…)")}
            />
            <select
              className={`${inputCls} max-w-[120px]`}
              value={r.calc}
              onChange={(e) => setRow(setRules, i, { calc: e.target.value })}
            >
              <option value="percent">%</option>
              <option value="flat">{t("Monto fijo")}</option>
            </select>
            <input
              type="number"
              min="0"
              step="0.01"
              className={`${inputCls} max-w-[110px]`}
              value={r.value}
              onChange={(e) => setRow(setRules, i, { value: e.target.value })}
              placeholder={r.calc === "percent" ? t("% (ej 10)") : t("Monto")}
            />
            <input
              type="number"
              className={`${inputCls} max-w-[90px]`}
              value={r.priority}
              onChange={(e) => setRow(setRules, i, { priority: e.target.value })}
              placeholder={t("Prior.")}
              title={t("Prioridad (mayor primero)")}
            />
            <button
              type="button"
              className="h-9 px-2 text-fg-muted hover:text-status-error"
              onClick={() => removeRow(setRules, i)}
              aria-label={t("Quitar")}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-sm font-medium text-fg">
            {t("Vigente desde (opcional)")}
          </label>
          <p className="text-xs text-fg-muted mb-1">
            {t("Vacío = rige desde ahora. Cada edición crea una versión nueva.")}
          </p>
          <input
            type="date"
            className={`${inputCls} max-w-[180px]`}
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={saving}
          iconLeft={<Save className="w-4 h-4" />}
        >
          {saving ? t("Guardando…") : t("Guardar tarifas")}
        </Button>
      </div>
    </div>
  );
}
